// Lightweight typed wrapper around the Telegram WebApp SDK loaded via <script>.

interface TelegramThemeParams {
  bg_color?: string;
  text_color?: string;
  hint_color?: string;
  link_color?: string;
  button_color?: string;
  button_text_color?: string;
  secondary_bg_color?: string;
}

export interface TelegramWebApp {
  initData: string;
  initDataUnsafe: {
    user?: { id: number; username?: string; first_name?: string };
    /** Passed via t.me/<bot>/<app>?startapp=… or via /start <param> */
    start_param?: string;
  };
  themeParams: TelegramThemeParams;
  colorScheme: 'light' | 'dark';
  /** Host platform: 'ios' | 'android' | 'macos' | 'tdesktop' | 'web' | 'unknown' */
  platform: string;
  expand(): void;
  close(): void;
  ready(): void;
  HapticFeedback?: {
    impactOccurred(style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft'): void;
    notificationOccurred(type: 'error' | 'success' | 'warning'): void;
    selectionChanged(): void;
  };
  MainButton: {
    text: string;
    isVisible: boolean;
    show(): void;
    hide(): void;
    setText(t: string): void;
    onClick(cb: () => void): void;
    offClick(cb: () => void): void;
    enable(): void;
    disable(): void;
  };
  BackButton: {
    isVisible: boolean;
    show(): void;
    hide(): void;
    onClick(cb: () => void): void;
    offClick(cb: () => void): void;
  };
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TelegramWebApp };
  }
}

export function getWebApp(): TelegramWebApp | null {
  return window.Telegram?.WebApp ?? null;
}

/** Call once on startup. */
export function bootstrapTelegram(): TelegramWebApp | null {
  const tg = getWebApp();
  if (!tg) {
    console.warn('Not running inside Telegram — running standalone (initData will be empty)');
    return null;
  }
  tg.ready();
  tg.expand();
  return tg;
}

export function haptic(style: 'light' | 'medium' | 'heavy' = 'medium'): void {
  getWebApp()?.HapticFeedback?.impactOccurred(style);
}

export function notify(type: 'success' | 'error' | 'warning'): void {
  getWebApp()?.HapticFeedback?.notificationOccurred(type);
}

/**
 * Best-effort iOS detection. Prefers Telegram's WebApp.platform when
 * available; falls back to user-agent sniffing for non-Telegram contexts
 * (e.g. running in Safari for dev). Used to gate iOS-specific UX hints.
 */
export function isIOS(): boolean {
  const tg = getWebApp();
  if (tg?.platform === 'ios') return true;
  const ua = navigator.userAgent || '';
  if (/iPhone|iPad|iPod/.test(ua)) return true;
  // iPadOS 13+ reports as Mac with touch points
  if (navigator.platform === 'MacIntel' && (navigator as Navigator & { maxTouchPoints?: number }).maxTouchPoints! > 1) {
    return true;
  }
  return false;
}
